import { useCallback, useMemo, useState } from "react";

import { type ApprovalConfig, type CloudFlowNodeType, NotificationProviders, TimeUnits } from "@doitintl/cmp-models";
import { Checkbox, FormControlLabel, Stack, Typography } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";

import { cloudflowTexts } from "../../../../../../assets/texts";
import { ReferencedTextAreaStandalone } from "../../../../Components/ReferencedTextArea/ReferencedTextArea";
import { useReferencedFieldContext } from "../../../ApiActionParametersForm/parameters/wrappers/ReferencedField/useReferencedFieldContext";
import { createReferencedTextAreaSchema } from "../../../ApiActionParametersForm/useApiActionParametersSchema";
import { FormChangesListener } from "../../../Common/FormChangesListener";
import { type NodeWitOutputModel } from "../../../Common/hooks/useReferenceableNodes";
import { useNodeConfigurationContext } from "../../NodeConfigurationContext";
import { useErrorUpdates, useInitialErrors, useSlackChannels } from "../hooks";
import { AutoRejectApproval } from "./AutoRejectApproval";
import { NotificationProvider } from "./NotificationProvider";

const createValidationSchema = (referenceableNodes: NodeWitOutputModel[]) =>
  yup.object({
    required: yup.boolean().default(false),
    recipient: yup.object({
      notificationProvider: yup
        .string()
        .oneOf([NotificationProviders.EMAIL, NotificationProviders.SLACK])
        .required()
        .default(NotificationProviders.EMAIL),
      emails: yup
        .array()
        .nullable()
        .default([])
        .when(["notificationProvider", "$required"], ([provider, required], schema) =>
          provider === NotificationProviders.EMAIL && required
            ? schema.min(1, "Valid email required").of(yup.string().email("Valid email required"))
            : schema
        ),
      slackChannels: yup
        .array()
        .nullable()
        .default([])
        .when(["notificationProvider", "$required"], ([provider, required], schema) =>
          provider === NotificationProviders.SLACK && required ? schema.min(1, "Slack channel required") : schema
        )
        .label("Error from the slack channels"),
    }),
    message: createReferencedTextAreaSchema({
      referenceableNodes,
    }).label("Message"),
    rejectApprovalAfterTime: yup.boolean().default(false),
    rejectTimeValue: yup.number().when("rejectApprovalAfterTime", {
      is: true,
      then: (schema) =>
        schema
          .transform((value) => (isNaN(Number(value)) ? undefined : Number(value)))
          .test("is-valid-number", "A valid positive number is required", (value, context) => {
            if (!context.parent.rejectApprovalAfterTime) return true;
            return value !== undefined && Number.isInteger(value) && value > 0;
          }),
      otherwise: (schema) => schema,
    }),
    rejectTimeUnit: yup.string().when("rejectApprovalAfterTime", {
      is: true,
      then: (schema) =>
        schema.test("is-valid-time-unit", "Time unit required", (value, context) => {
          if (!context.parent.rejectApprovalAfterTime) return true;
          return (
            value !== undefined &&
            value !== null &&
            [TimeUnits.Days, TimeUnits.Hours, TimeUnits.Weeks, TimeUnits.Months].includes(value as TimeUnits)
          );
        }),
      otherwise: (schema) => schema,
    }),
  });

const Approval = () => {
  const { nodeConfig, updateNode } = useNodeConfigurationContext<CloudFlowNodeType.ACTION>();
  const { referenceableNodes } = useReferencedFieldContext();

  const validationSchema = useMemo(() => createValidationSchema(referenceableNodes), [referenceableNodes]);

  const initialValues = validationSchema.cast(nodeConfig.approval);
  const initialErrors = useInitialErrors(validationSchema, initialValues);

  const [isApprovalValid, setIsApprovalValid] = useState(() => validationSchema.isValidSync(initialValues));
  const isSlackProvider = nodeConfig.approval?.recipient?.notificationProvider === NotificationProviders.SLACK;
  const { slackChannels, channelsLoading } = useSlackChannels(isSlackProvider);

  useErrorUpdates<CloudFlowNodeType.ACTION>({
    isValid: isApprovalValid,
    errorKey: "approval_error",
    errorMessage: cloudflowTexts.STATUS.APPROVAL_ERROR,
  });

  const onValuesChange = useCallback(
    (values: yup.InferType<typeof validationSchema>) => {
      const parameters = validationSchema.cast(values) as ApprovalConfig;
      updateNode((prev) => ({ approval: { ...prev.approval!, ...parameters } }));
    },
    [updateNode, validationSchema]
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={() => {}}
      validateOnMount
      validateOnBlur
      validateOnChange
      initialErrors={initialErrors}
    >
      {({ values, setFieldValue, setFieldTouched, errors, touched }) => (
        <Stack spacing={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
            Approval
          </Typography>

          <Typography variant="body2" color={"text.secondary"}>
            If you want this action to be approved by someone in your organization before it runs you can set it to
            require approval
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                name="required"
                checked={Boolean(values.required)}
                onChange={(e) => {
                  setFieldValue("required", e.target.checked);
                }}
              />
            }
            label="Require approval for this action"
          />

          {values.required && (
            <>
              <NotificationProvider loadingSlackChannels={channelsLoading} slackChannels={slackChannels} />
              <ReferencedTextAreaStandalone
                onChange={(e) => {
                  setFieldValue("message", e.target.value);
                }}
                onBlur={() => {
                  setFieldTouched("message", true);
                }}
                referenceableNodes={referenceableNodes}
                label="Message"
                name="message"
                value={values.message}
                error={touched.message && Boolean(errors.message)}
                helperText={touched.message ? errors.message?.toString() : undefined}
              />
              <AutoRejectApproval />
            </>
          )}
          <FormChangesListener onValidityChange={setIsApprovalValid} onValuesChange={onValuesChange} />
        </Stack>
      )}
    </Formik>
  );
};

export default Approval;
